import { Bell, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { appNavStyles as styles } from './AppNav.styles';
import { type AppNavPresenterProps } from './AppNav.types';

import { BrandMark } from '@/components/atoms/BrandMark';
import { ROUTES } from '@/app/routes';
import { Avatar } from '@/components/atoms/Avatar';
import { cn } from '@/lib/cn';

/** Desktop classique — barre haute : logo, liens, messages, avatar. */
export function AppNavTopBar({
  items,
  user,
  unread,
  onOpenNotifications,
  notificationsPanel,
  className,
}: Readonly<AppNavPresenterProps>) {
  const { t } = useTranslation('nav');

  return (
    <nav
      aria-label={t('aria.primary')}
      className={cn(styles.root({ layout: 'topbar' }), className)}
    >
      <span className={styles.brand({ layout: 'topbar' })}>
        <BrandMark className={styles.brandDot()} />
        Meeply
      </span>

      <div className="flex gap-5">
        {items.map(({ to, labelKey }) => (
          <NavLink
            key={to}
            to={to}
            className={cn(styles.item({ layout: 'topbar' }))}
          >
            {t(labelKey)}
          </NavLink>
        ))}
      </div>

      <div className="ml-auto flex items-center gap-3">
        {/* The bell opens the drop-down rather than navigating: the page is
            one click further, behind "See all". */}
        <div className="relative">
          <button
            type="button"
            onClick={onOpenNotifications}
            aria-haspopup="dialog"
            aria-label={
              unread.notifications > 0
                ? `${t('notifications')} — ${t('aria.unread', { count: unread.notifications })}`
                : t('notifications')
            }
            className={cn(styles.item({ layout: 'topbar' }), 'p-2')}
          >
            <Bell aria-hidden className={styles.icon({ layout: 'topbar' })} />
            {unread.notifications > 0 && (
              <span aria-hidden className={styles.badge({ layout: 'topbar' })}>
                {unread.notifications}
              </span>
            )}
          </button>
          {notificationsPanel}
        </div>

        <NavLink
          to={ROUTES.messages}
          className={cn(styles.item({ layout: 'topbar' }), 'p-2')}
          aria-label={
            unread.messages > 0
              ? `${t('messages')} — ${t('aria.unread', { count: unread.messages })}`
              : t('messages')
          }
        >
          <MessageCircle
            aria-hidden
            className={styles.icon({ layout: 'topbar' })}
          />
          {unread.messages > 0 && (
            <span aria-hidden className={styles.badge({ layout: 'topbar' })}>
              {unread.messages}
            </span>
          )}
        </NavLink>

        <NavLink
          to={ROUTES.settings}
          aria-label={t('profile')}
          className="rounded-full"
        >
          <Avatar src={user.avatarUrl} name={user.displayName} size="sm" />
        </NavLink>
      </div>
    </nav>
  );
}
